import React, { useState, useRef } from "react";
import { Turnstile } from "@marsidev/react-turnstile";
import PhoneNumberInput from "react-phone-number-input";
import { useForm } from "react-hook-form";
import { reset } from "redux-form";
import { Link } from "react-router-dom";

import ApplicationLayout, { applicationStyles as styles } from "../../TalentApplication/ApplicationLayout";
import WithUserForm from "../../Register/WithUserForm";
import { SubmitError } from "../../TalentApplication/fields";
import { CountryCode, formatPhoneNumber } from "../../TalentApplication/StepDetails_1st";
import { EMAIL_REQUIRED, emailFormat, PHONE_REQUIRED, phoneFormat } from "../../TalentApplication/StepDetails_1st/validators";
import { libphonenumberFilteredMetadata } from "../../TalentApplication/StepDetails_1st/twilio-geo-permissions";

// Save login details to avoid empty form when coming back from verification page
const SAVED_DETAILS = "webLoginDetails";

const readSavedDetails = () => {
  try {
    return JSON.parse(window.sessionStorage.getItem(SAVED_DETAILS)) || {};
  } catch (e) {
    return {};
  }
};

const saveDetails = (details) => {
  try {
    window.sessionStorage.setItem(SAVED_DETAILS, JSON.stringify(details));
  } catch (e) {}
};

const WebLogin = () => {
    const {
        register,
        formState: { errors },
        setError,
        clearErrors,
        trigger
    } = useForm();

    const saved = useRef(readSavedDetails()).current;
    const [email, setEmail] = useState(saved.email || "");
    const [phoneNumber, setPhoneNumber] = useState(saved.phoneNumber || "");
    const [isAppReviewerEmail, setIsAppReviewerEmail] = useState(false);

    const TURNSTILE_ENABLED = process.env.REACT_APP_TURNSTILE_ENABLED === 'true';
    const turnstileRef = useRef(null);
    const isSubmittingRef = useRef(false);
    const metadata = useRef(libphonenumberFilteredMetadata()).current;

    const emailField = register("email", {
        required: EMAIL_REQUIRED,
        validate: (value) => emailFormat(value) || true,
    });

    const getTurnstileToken = async () => {
        if (!TURNSTILE_ENABLED) return null;
        const widget = turnstileRef.current;
        widget.reset();
        widget.execute();
        const token = await widget.getResponsePromise();
        widget.reset();
        return token;
    };

    const webLogin = async (props) => {
        // Guard against the double-submit caused by the form/button both firing handleSubmit,
        // otherwise the login sends two SMS and two emails.
        if (isSubmittingRef.current) return;
        isSubmittingRef.current = true;
        try {
            clearErrors(["response"]);
            const valid = await trigger();
            if (!valid) return;
            if (!phoneNumber) {
                setError("phoneNumber", { message: PHONE_REQUIRED });
                return;
            }
            const phoneError = phoneFormat(phoneNumber);
            if (phoneError) {
                setError("phoneNumber", { message: phoneError });
                return;
            }
            let turnstileToken;
            try {
                turnstileToken = await getTurnstileToken();
            } catch {
                setError("response", { message: "We couldn't verify you're human. Please try again." });
                return;
            }
            const response = await props.webLogin({
                variables: {
                    email,
                    phoneNumber,
                    turnstileToken
                },
            });
            saveDetails({ email, phoneNumber });
            window.location.href = `${window.location.origin}/apply/verification?session=${encodeURIComponent(response.data.webLogin.token)}`;
        } catch (error) {
            setError("response", { message: error.message });
        } finally {
            isSubmittingRef.current = false;
        }
    };

    const onPhoneChange = (value) => {
        setPhoneNumber(formatPhoneNumber(value));
        clearErrors(["phoneNumber", "response"]);
    };

    return (
        <WithUserForm
            formRequiresAuthentication={false}
            onSubmit={(values, dispatch, props) => webLogin(props)}
        >
            {({ form: { handleSubmit }, dispatch }) => {
                const footer = (
                    <>
                        {errors.response && (
                            <SubmitError>{errors.response.message}</SubmitError>
                        )}
                        <div className={styles.actions}>
                            <button type="submit" className={styles.cta} disabled={isAppReviewerEmail}>
                                Sign in
                            </button>
                            <p className={styles.footerNote}>
                                Having trouble? <a href="https://wa.me/443330166335">Contact us</a>
                            </p>
                        </div>
                    </>
                );

                return (
                    <ApplicationLayout
                        as="form"
                        onSubmit={handleSubmit}
                        title="Continue application"
                        intro={<>Don't have an account? <Link to="/apply" onClick={() => dispatch(reset("apply"))}>Apply for membership</Link></>}
                        footer={footer}
                    >
                        <div className={styles.field}>
                            <label className={styles.fieldLabel} htmlFor="email">Email</label>
                            <div className={`${styles.box} ${styles.inputBox} ${errors.email ? styles.boxInvalid : ""}`}>
                                <input
                                    {...emailField}
                                    id="email"
                                    type="text"
                                    inputMode="email"
                                    autoComplete="email"
                                    autoCapitalize="off"
                                    spellCheck="false"
                                    defaultValue={saved.email || ""}
                                    className={styles.input}
                                    onChange={(e) => {
                                        emailField.onChange(e);
                                        setIsAppReviewerEmail(
                                            (process.env.REACT_APP_APP_REVIEWER_EMAILS || '')
                                                .split(',')
                                                .map((one) => one.trim())
                                                .includes(e.target.value)
                                        );
                                        setEmail(e.target.value);
                                        clearErrors(["email", "response"]);
                                    }}
                                />
                            </div>
                            {errors.email && <div className={styles.error}>{errors.email.message}</div>}
                            {isAppReviewerEmail && (
                                <div className={styles.error}>
                                    You've been identified as an app reviewer. Please log in using the mobile app.
                                </div>
                            )}
                        </div>

                        {!isAppReviewerEmail && (
                            <div className={styles.field}>
                                <label className={styles.fieldLabel} htmlFor="phoneNumber">Mobile number</label>
                                <div className={`${styles.box} ${styles.phoneField} ${errors.phoneNumber ? styles.boxInvalid : ""}`}>
                                    <PhoneNumberInput
                                        id="phoneNumber"
                                        name="phoneNumber"
                                        placeholder="07123 456789"
                                        country="GB"
                                        international={false}
                                        displayInitialValueAsLocalNumber
                                        limitMaxLength
                                        metadata={metadata}
                                        type="tel"
                                        value={phoneNumber}
                                        onChange={onPhoneChange}
                                        flagComponent={CountryCode}
                                    />
                                </div>
                                {errors.phoneNumber && (
                                    <div className={styles.error}>{errors.phoneNumber.message}</div>
                                )}
                            </div>
                        )}

                        {TURNSTILE_ENABLED && (
                            <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 1000 }}>
                                <Turnstile
                                    ref={turnstileRef}
                                    siteKey={process.env.REACT_APP_TURNSTILE_SITE_KEY}
                                    options={{ appearance: 'interaction-only', execution: 'execute' }}
                                />
                            </div>
                        )}
                    </ApplicationLayout>
                );
            }}
        </WithUserForm>
    );
};

export default WebLogin;
